import { getCurrentUser } from "./authService";
import { Message } from "../components/messaging/BookingMessageBox";

// Define message types
export type MessageType =
  | "NEW_MESSAGE"
  | "READ_RECEIPT"
  | "USER_TYPING"
  | "PING"
  | "PONG"
  | "SYSTEM"
  | "TEST_MESSAGE"
  | "TEST_RESPONSE"
  | "MESSAGE_DELIVERED"
  | "UNREAD_COUNT_UPDATE"
  | "CONVERSATIONS_UPDATE"
  | "REQUEST_UNREAD_COUNT"
  | "REQUEST_CONVERSATIONS"
  | "ADMIN_MESSAGE_SENT"
  | "ADMIN_MESSAGE_READ"
  | "ADMIN_CONVERSATION_UPDATE"
  | "REQUEST_ADMIN_MESSAGES"
  | "ADMIN_UNREAD_COUNT_UPDATE"
  | "CHECKLIST_ITEM_COMPLETED"
  | "CHECKLIST_PHOTO_UPLOADED"
  | "CLEANING_PROGRESS_UPDATE";

// Define message interface
export interface WebSocketMessage {
  type: MessageType;
  payload: any;
}

// Define event interfaces for type safety
export interface NewMessageEvent {
  messageId: string;
  bookingId: string;
  bookingUuid: string;
  message: Message;
}

export interface ReadReceiptEvent {
  messageId: string;
  bookingId: string;
  readAt: Date;
}

export interface UnreadCountEvent {
  count: number;
}

export interface ConversationsEvent {
  conversations: any[];
}

export interface AdminMessageEvent {
  message: {
    uuidId: string;
    senderId: number;
    recipientId: number;
    subject?: string;
    content: string;
    isRead: boolean;
    createdAt: string;
    sender: {
      id: number;
      name: string;
      profilePic?: string;
      role: string;
    };
    recipient: {
      id: number;
      name: string;
      profilePic?: string;
      role: string;
    };
  };
}

export interface AdminMessageReadEvent {
  messageId: string;
  readBy: number;
  readAt: string;
}

export interface AdminUnreadCountEvent {
  count: number;
}

// Checklist event interfaces
export interface ChecklistItemCompletedEvent {
  bookingId: string;
  bookingUuid: string;
  checklistId: number;
  stepIndex: number;
  area: string;
  stepName: string;
  completedBy: {
    id: number;
    name: string;
  };
  completedAt: string;
  notes?: string;
}

export interface ChecklistPhotoUploadedEvent {
  bookingId: string;
  bookingUuid: string;
  checklistId: number;
  stepIndex: number;
  area: string;
  photoId: number;
  photoUrl: string;
  photoType: "before" | "after";
  uploadedBy: {
    id: number;
    name: string;
  };
  uploadedAt: string;
}

export interface CleaningProgressEvent {
  bookingId: string;
  bookingUuid: string;
  totalTasks: number;
  completedTasks: number;
  completionPercentage: number;
  completedAreas: string[];
  inProgressAreas: string[];
  remainingAreas: string[];
}

// Define callback types
export type WebSocketEventCallback = (data: any) => void;
export type MessageEventCallback = (data: NewMessageEvent) => void;
export type ReadReceiptEventCallback = (data: ReadReceiptEvent) => void;
export type UnreadCountEventCallback = (data: UnreadCountEvent) => void;
export type ConversationsEventCallback = (data: ConversationsEvent) => void;
export type AdminMessageEventCallback = (data: AdminMessageEvent) => void;
export type AdminMessageReadEventCallback = (
  data: AdminMessageReadEvent
) => void;
export type AdminUnreadCountEventCallback = (
  data: AdminUnreadCountEvent
) => void;
export type ChecklistItemCompletedEventCallback = (
  data: ChecklistItemCompletedEvent
) => void;
export type ChecklistPhotoUploadedEventCallback = (
  data: ChecklistPhotoUploadedEvent
) => void;
export type CleaningProgressEventCallback = (
  data: CleaningProgressEvent
) => void;
export type WebSocketUnsubscribe = () => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectInterval: number = 3000; // ms
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5; // Reduced from 10 to 5
  private pingInterval: NodeJS.Timeout | null = null;
  private isAuthenticated: boolean = false;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectDelay = 2000; // Increased from 1000 to 2000
  private url: string;
  private eventHandlers: Map<string, Set<WebSocketEventCallback>> = new Map();
  private connectionPromise: Promise<void> | null = null;
  private isIntentionallyDisconnected: boolean = false;

  // Message-specific event listeners for more granular control
  private messageListeners: Map<string, Set<MessageEventCallback>> = new Map();
  private readReceiptListeners: Map<string, Set<ReadReceiptEventCallback>> =
    new Map();
  private unreadCountListeners: Set<UnreadCountEventCallback> = new Set();
  private conversationsListeners: Set<ConversationsEventCallback> = new Set();

  // Admin message listeners
  private adminMessageListeners: Set<AdminMessageEventCallback> = new Set();
  private adminMessageReadListeners: Set<AdminMessageReadEventCallback> =
    new Set();
  private adminUnreadCountListeners: Set<AdminUnreadCountEventCallback> =
    new Set();

  // Checklist real-time listeners (booking-specific)
  private checklistItemCompletedListeners: Map<string, Set<ChecklistItemCompletedEventCallback>> = new Map();
  private checklistPhotoUploadedListeners: Map<string, Set<ChecklistPhotoUploadedEventCallback>> = new Map();
  private cleaningProgressListeners: Map<string, Set<CleaningProgressEventCallback>> = new Map();

  constructor() {
    // WebSocket endpoint uses the same origin as the REST API
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const baseWsUrl = `${wsProtocol}//${window.location.host}`;
    this.url = `${baseWsUrl}/ws/messages`;
  }

  /**
   * Connect to the WebSocket server
   * @returns Promise that resolves when connected or rejects on error
   */
  connect(): Promise<void> {
    // If we already have a connection promise pending, return it
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // If already connected, resolve immediately
    if (this.socket?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // Reset intentional disconnect flag when attempting to connect
    this.isIntentionallyDisconnected = false;

    this.connectionPromise = new Promise<void>((resolve, reject) => {
      try {
        if (this.socket) {
          this.cleanupSocket();
        }

        // Create WebSocket connection
        this.socket = new WebSocket(this.url);

        const connectionTimeout = setTimeout(() => {
          if (this.socket?.readyState !== WebSocket.OPEN) {
            this.socket?.close();
            reject(new Error("Connection timeout"));
          }
        }, 10000); // 10 second timeout

        this.socket.onopen = (event) => {
          clearTimeout(connectionTimeout);
          this.handleOpen(event);
          resolve();
        };

        this.socket.onmessage = this.handleMessage.bind(this);
        this.socket.onclose = this.handleClose.bind(this);
        this.socket.onerror = (event) => {
          clearTimeout(connectionTimeout);
          this.handleError(event);
          reject(new Error("WebSocket connection failed"));
        };
      } catch (error) {
        console.error("Failed to initiate WebSocket connection:", error);
        this.socket = null;
        this.scheduleReconnect();
        reject(error);
      }
    }).finally(() => {
      this.connectionPromise = null;
    });

    return this.connectionPromise;
  }

  private cleanupSocket(): void {
    if (!this.socket) return;

    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;

    if (this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close();
    }
  }

  /**
   * Handle WebSocket connection open
   */
  private handleOpen(event: Event): void {
    console.log("WebSocket connection established");
    this.reconnectAttempts = 0;
    this.isAuthenticated = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.pingInterval) clearInterval(this.pingInterval);
    this.pingInterval = setInterval(() => {
      this.sendPing();
    }, 30000);

    this.emit("open", event);
    this.emit("connected");
  }

  /**
   * Send a PING message
   */
  private sendPing(): void {
    if (this.isConnected()) {
      this.send({
        type: "PING",
        payload: { timestamp: new Date().toISOString() },
      });
    } else {
      console.warn("Attempted to send PING on disconnected socket.");
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    }
  }

  /**
   * Handle WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as WebSocketMessage;

      if (data.type !== "PONG") {
        console.debug("WebSocket message received:", data.type, data.payload);
      }

      // Handle specific message types
      switch (data.type) {
        case "NEW_MESSAGE":
          this.handleNewMessage(data.payload);
          break;
        case "READ_RECEIPT":
          this.handleReadReceipt(data.payload);
          break;
        case "UNREAD_COUNT_UPDATE":
          this.handleUnreadCountUpdate(data.payload);
          break;
        case "CONVERSATIONS_UPDATE":
          this.handleConversationsUpdate(data.payload);
          break;
        case "ADMIN_MESSAGE_SENT":
          this.handleAdminMessage(data.payload);
          break;
        case "ADMIN_MESSAGE_READ":
          this.handleAdminMessageRead(data.payload);
          break;
        case "ADMIN_UNREAD_COUNT_UPDATE":
          this.handleAdminUnreadCountUpdate(data.payload);
          break;
        case "CHECKLIST_ITEM_COMPLETED":
          this.handleChecklistItemCompleted(data.payload);
          break;
        case "CHECKLIST_PHOTO_UPLOADED":
          this.handleChecklistPhotoUploaded(data.payload);
          break;
        case "CLEANING_PROGRESS_UPDATE":
          this.handleCleaningProgressUpdate(data.payload);
          break;
      }

      this.emit(data.type.toLowerCase(), data.payload);
      this.emit("message", data);
    } catch (error) {
      console.error(
        "Error parsing WebSocket message:",
        error,
        "Raw data:",
        event.data
      );
      this.emit("error", new Error("Failed to parse message"));
    }
  }

  // Message-specific event handlers
  private handleNewMessage(payload: any): void {
    if (!payload || !payload.bookingUuid) return;

    const bookingId = payload.bookingUuid;
    const listeners = this.messageListeners.get(bookingId);

    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback({
            messageId: payload.uuidId,
            bookingId: payload.bookingUuid,
            bookingUuid: payload.bookingUuid,
            message: payload,
          });
        } catch (error) {
          console.error("Error in new message callback:", error);
        }
      });
    }
  }

  private handleReadReceipt(payload: any): void {
    if (!payload || !payload.bookingId) return;

    const bookingId = payload.bookingId;
    const listeners = this.readReceiptListeners.get(bookingId);

    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback({
            messageId: payload.messageId,
            bookingId: payload.bookingId,
            readAt: new Date(payload.readAt),
          });
        } catch (error) {
          console.error("Error in read receipt callback:", error);
        }
      });
    }
  }

  private handleUnreadCountUpdate(payload: any): void {
    this.unreadCountListeners.forEach((callback) => {
      try {
        callback({ count: payload.count });
      } catch (error) {
        console.error("Error in unread count callback:", error);
      }
    });
  }

  private handleConversationsUpdate(payload: any): void {
    this.conversationsListeners.forEach((callback) => {
      try {
        callback({ conversations: payload.conversations });
      } catch (error) {
        console.error("Error in conversations update callback:", error);
      }
    });
  }

  private handleAdminMessage(payload: any): void {
    this.adminMessageListeners.forEach((callback) => {
      try {
        callback({ message: payload.message });
      } catch (error) {
        console.error("Error in admin message callback:", error);
      }
    });
  }

  private handleAdminMessageRead(payload: any): void {
    this.adminMessageReadListeners.forEach((callback) => {
      try {
        callback({
          messageId: payload.messageId,
          readBy: payload.readBy,
          readAt: payload.readAt,
        });
      } catch (error) {
        console.error("Error in admin message read callback:", error);
      }
    });
  }

  private handleAdminUnreadCountUpdate(payload: any): void {
    this.adminUnreadCountListeners.forEach((callback) => {
      try {
        callback({ count: payload.count });
      } catch (error) {
        console.error("Error in admin unread count callback:", error);
      }
    });
  }

  // Checklist event handlers
  private handleChecklistItemCompleted(payload: ChecklistItemCompletedEvent): void {
    if (!payload || !payload.bookingUuid) return;

    const bookingId = payload.bookingUuid;
    const listeners = this.checklistItemCompletedListeners.get(bookingId);

    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error("Error in checklist item completed callback:", error);
        }
      });
    }
  }

  private handleChecklistPhotoUploaded(payload: ChecklistPhotoUploadedEvent): void {
    if (!payload || !payload.bookingUuid) return;

    const bookingId = payload.bookingUuid;
    const listeners = this.checklistPhotoUploadedListeners.get(bookingId);

    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error("Error in checklist photo uploaded callback:", error);
        }
      });
    }
  }

  private handleCleaningProgressUpdate(payload: CleaningProgressEvent): void {
    if (!payload || !payload.bookingUuid) return;

    const bookingId = payload.bookingUuid;
    const listeners = this.cleaningProgressListeners.get(bookingId);

    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(payload);
        } catch (error) {
          console.error("Error in cleaning progress update callback:", error);
        }
      });
    }
  }

  /**
   * Handle WebSocket connection close
   */
  private handleClose(event: CloseEvent): void {
    if (this.socket !== event.target) return;

    console.log(
      `WebSocket connection closed: Code=${event.code}, Reason=${
        event.reason || "No reason given"
      }`
    );
    const wasConnected = this.isConnected();
    this.socket = null;

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    this.emit("close", event);
    if (wasConnected) {
      this.emit("disconnected");
    }

    if (event.code !== 1000 && event.code !== 1001 && event.code !== 1005) {
      this.scheduleReconnect();
    } else {
      console.log(
        "WebSocket closed normally or without status, not reconnecting automatically."
      );
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Handle WebSocket error
   */
  private handleError(event: Event): void {
    if (this.socket !== event.target && event.target !== null) return;

    let errorMessage = "WebSocket error occurred";
    if (event instanceof ErrorEvent && event.message) {
      errorMessage = event.message;
    } else if (event.type) {
      errorMessage = `WebSocket error event: ${event.type}`;
    }

    console.error("WebSocket error:", errorMessage, event);
    this.emit("error", event);

    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close(1011, "Error occurred");
    } else {
      this.handleClose(
        new CloseEvent("close", { code: 1011, reason: "Error occurred" })
      );
    }
  }

  /**
   * Send a message through the WebSocket
   */
  send(message: WebSocketMessage): boolean {
    if (!this.isConnected()) {
      console.warn(
        "Attempted to send message on a disconnected WebSocket:",
        message.type
      );
      return false;
    }

    try {
      this.socket!.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("Error sending WebSocket message:", error);
      this.emit("error", new Error("Failed to send message"));
      if (this.socket) {
        this.socket.close(1011, "Send failed");
      }
      return false;
    }
  }

  /**
   * Subscribe to WebSocket events
   * @param event The event name to subscribe to (will be lowercased)
   * @param callback Function to call when the event is received
   */
  on(event: string, callback: WebSocketEventCallback): void {
    const eventName = event.toLowerCase();
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set());
    }
    this.eventHandlers.get(eventName)?.add(callback);
  }

  /**
   * Unsubscribe from WebSocket events
   * @param event The event name to unsubscribe from (will be lowercased)
   * @param callback The callback function to remove
   */
  off(event: string, callback: WebSocketEventCallback): void {
    const eventName = event.toLowerCase();
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.delete(callback);
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventName);
      }
    }
  }

  /**
   * Internal method to emit events to listeners
   */
  private emit(event: string, data?: any): void {
    const eventName = event.toLowerCase();
    const handlers = this.eventHandlers.get(eventName);
    if (handlers) {
      handlers.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(
            `Error in WebSocket event handler for "${eventName}":`,
            error
          );
        }
      });
    }
  }

  /**
   * Send typing status update
   */
  sendTypingStatus(recipientId: string, isTyping: boolean): void {
    this.send({
      type: "USER_TYPING",
      payload: {
        recipientId,
        isTyping,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Send read receipt
   */
  sendReadReceipt(messageId: string): void {
    this.send({
      type: "READ_RECEIPT",
      payload: {
        messageId,
        timestamp: new Date().toISOString(),
      },
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    console.log("Disconnecting WebSocket intentionally...");
    this.isIntentionallyDisconnected = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    this.reconnectAttempts = 0;

    if (this.socket) {
      this.cleanupSocket();
    }

    if (this.isConnected()) {
      this.emit("disconnected");
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  private scheduleReconnect() {
    // Don't reconnect if intentionally disconnected
    if (this.isIntentionallyDisconnected) {
      console.log("Not reconnecting - connection was intentionally closed");
      return;
    }

    if (
      this.reconnectTimeout ||
      this.reconnectAttempts >= this.maxReconnectAttempts
    ) {
      if (
        !this.reconnectTimeout &&
        this.reconnectAttempts >= this.maxReconnectAttempts
      ) {
        console.warn("Max WebSocket reconnect attempts reached. Giving up.");
        this.emit("reconnect_failed");
      }
      return;
    }

    this.reconnectAttempts++;
    const baseDelay = Math.min(
      this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1),
      30000
    );
    const jitter = baseDelay * 0.1 * Math.random(); // Reduced jitter
    const delay = baseDelay + jitter;

    console.log(
      `Scheduling reconnect attempt ${this.reconnectAttempts}/${
        this.maxReconnectAttempts
      } in ${delay.toFixed(0)}ms`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (!this.isIntentionallyDisconnected) {
        this.connect().catch((err) => {
          console.error(`Reconnect attempt failed:`, err.message);
        });
      }
    }, delay);
  }

  // Subscribe to new messages for a specific booking
  subscribeToMessages(
    bookingId: string,
    callback: MessageEventCallback
  ): WebSocketUnsubscribe {
    if (!this.messageListeners.has(bookingId)) {
      this.messageListeners.set(bookingId, new Set());
    }

    this.messageListeners.get(bookingId)?.add(callback);

    return () => {
      const listeners = this.messageListeners.get(bookingId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.messageListeners.delete(bookingId);
        }
      }
    };
  }

  // Subscribe to read receipts for a specific booking
  subscribeToReadReceipts(
    bookingId: string,
    callback: ReadReceiptEventCallback
  ): WebSocketUnsubscribe {
    if (!this.readReceiptListeners.has(bookingId)) {
      this.readReceiptListeners.set(bookingId, new Set());
    }

    this.readReceiptListeners.get(bookingId)?.add(callback);

    return () => {
      const listeners = this.readReceiptListeners.get(bookingId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.readReceiptListeners.delete(bookingId);
        }
      }
    };
  }

  // Subscribe to unread count updates
  subscribeToUnreadCount(
    callback: UnreadCountEventCallback
  ): WebSocketUnsubscribe {
    this.unreadCountListeners.add(callback);

    return () => {
      this.unreadCountListeners.delete(callback);
    };
  }

  // Subscribe to conversations list updates
  subscribeToConversations(
    callback: ConversationsEventCallback
  ): WebSocketUnsubscribe {
    this.conversationsListeners.add(callback);

    return () => {
      this.conversationsListeners.delete(callback);
    };
  }

  // Request an immediate unread count update
  requestUnreadCount(): void {
    if (this.isConnected()) {
      this.send({
        type: "REQUEST_UNREAD_COUNT",
        payload: { timestamp: new Date().toISOString() },
      });
    }
  }

  // Request an immediate conversations list update
  requestConversations(): void {
    if (this.isConnected()) {
      this.send({
        type: "REQUEST_CONVERSATIONS",
        payload: { timestamp: new Date().toISOString() },
      });
    }
  }

  // Subscribe to admin messages
  subscribeToAdminMessages(
    callback: AdminMessageEventCallback
  ): WebSocketUnsubscribe {
    this.adminMessageListeners.add(callback);

    return () => {
      this.adminMessageListeners.delete(callback);
    };
  }

  // Subscribe to admin message read receipts
  subscribeToAdminMessageRead(
    callback: AdminMessageReadEventCallback
  ): WebSocketUnsubscribe {
    this.adminMessageReadListeners.add(callback);

    return () => {
      this.adminMessageReadListeners.delete(callback);
    };
  }

  // Subscribe to admin unread count updates
  subscribeToAdminUnreadCount(
    callback: AdminUnreadCountEventCallback
  ): WebSocketUnsubscribe {
    this.adminUnreadCountListeners.add(callback);

    return () => {
      this.adminUnreadCountListeners.delete(callback);
    };
  }

  // Request admin messages update
  requestAdminMessages(): void {
    if (this.isConnected()) {
      this.send({
        type: "REQUEST_ADMIN_MESSAGES",
        payload: { timestamp: new Date().toISOString() },
      });
    }
  }

  // ============================================
  // Checklist Real-time Subscription Methods
  // ============================================

  /**
   * Subscribe to checklist item completion events for a specific booking
   * @param bookingId The booking UUID to listen for updates
   * @param callback Function to call when a checklist item is completed
   * @returns Unsubscribe function
   */
  subscribeToChecklistItemCompleted(
    bookingId: string,
    callback: ChecklistItemCompletedEventCallback
  ): WebSocketUnsubscribe {
    if (!this.checklistItemCompletedListeners.has(bookingId)) {
      this.checklistItemCompletedListeners.set(bookingId, new Set());
    }

    this.checklistItemCompletedListeners.get(bookingId)?.add(callback);

    return () => {
      const listeners = this.checklistItemCompletedListeners.get(bookingId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.checklistItemCompletedListeners.delete(bookingId);
        }
      }
    };
  }

  /**
   * Subscribe to checklist photo upload events for a specific booking
   * @param bookingId The booking UUID to listen for updates
   * @param callback Function to call when a photo is uploaded
   * @returns Unsubscribe function
   */
  subscribeToChecklistPhotoUploaded(
    bookingId: string,
    callback: ChecklistPhotoUploadedEventCallback
  ): WebSocketUnsubscribe {
    if (!this.checklistPhotoUploadedListeners.has(bookingId)) {
      this.checklistPhotoUploadedListeners.set(bookingId, new Set());
    }

    this.checklistPhotoUploadedListeners.get(bookingId)?.add(callback);

    return () => {
      const listeners = this.checklistPhotoUploadedListeners.get(bookingId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.checklistPhotoUploadedListeners.delete(bookingId);
        }
      }
    };
  }

  /**
   * Subscribe to cleaning progress updates for a specific booking
   * @param bookingId The booking UUID to listen for updates
   * @param callback Function to call when progress is updated
   * @returns Unsubscribe function
   */
  subscribeToCleaningProgress(
    bookingId: string,
    callback: CleaningProgressEventCallback
  ): WebSocketUnsubscribe {
    if (!this.cleaningProgressListeners.has(bookingId)) {
      this.cleaningProgressListeners.set(bookingId, new Set());
    }

    this.cleaningProgressListeners.get(bookingId)?.add(callback);

    return () => {
      const listeners = this.cleaningProgressListeners.get(bookingId);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.cleaningProgressListeners.delete(bookingId);
        }
      }
    };
  }

  /**
   * Subscribe to all checklist-related events for a specific booking
   * Convenience method that subscribes to item completion, photo uploads, and progress updates
   * @param bookingId The booking UUID to listen for updates
   * @param callbacks Object containing callback functions for each event type
   * @returns Unsubscribe function that removes all subscriptions
   */
  subscribeToChecklistUpdates(
    bookingId: string,
    callbacks: {
      onItemCompleted?: ChecklistItemCompletedEventCallback;
      onPhotoUploaded?: ChecklistPhotoUploadedEventCallback;
      onProgressUpdate?: CleaningProgressEventCallback;
    }
  ): WebSocketUnsubscribe {
    const unsubscribers: WebSocketUnsubscribe[] = [];

    if (callbacks.onItemCompleted) {
      unsubscribers.push(
        this.subscribeToChecklistItemCompleted(bookingId, callbacks.onItemCompleted)
      );
    }

    if (callbacks.onPhotoUploaded) {
      unsubscribers.push(
        this.subscribeToChecklistPhotoUploaded(bookingId, callbacks.onPhotoUploaded)
      );
    }

    if (callbacks.onProgressUpdate) {
      unsubscribers.push(
        this.subscribeToCleaningProgress(bookingId, callbacks.onProgressUpdate)
      );
    }

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }
}

// Create a singleton instance
const websocketService = new WebSocketService();

export default websocketService;
